import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { DropdownFieldComponent } from '../dropdown-field/dropdown-field.component';
import { SelectFieldOption } from '../select-field-option';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';

/**
 * One choice out of many, as one signal-forms field.
 *
 * `pace-dropdown-field` covers this with `maxSelections = 1`, but its value stays a collection,
 * which leaves a form model holding `['0001']` where the state it edits holds `'0001'`. This wraps
 * it so the value is the chosen option itself — empty when nothing is chosen — and the model can
 * keep the same shape as the state, which is what lets it be written straight through to a store.
 *
 * ```html
 * <pace-select-field [formField]="signalForm.taskType" [options]="taskTypes()" />
 * ```
 *
 * A scalar also makes the schema say what it means: `required(path.taskType)` rather than
 * `minLength(path.taskType, 1)`, which is how "pick one" has to be phrased against a collection.
 *
 * Reach for `pace-dropdown-field` directly when more than one option may be chosen.
 */
@Component({
    selector: 'pace-select-field',
    templateUrl: './select-field.component.html',
    styleUrl: './select-field.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DropdownFieldComponent],
})
export class SelectFieldComponent extends SignalFieldBaseDirective<string> {
    /** the chosen option's value, or '' while nothing is chosen */
    public readonly value = model<string>('');

    public readonly options = input<readonly SelectFieldOption[]>([]);

    public readonly placeholder = input('');

    /** Forwarded: whether picking the chosen option again clears the field. */
    public readonly deselectable = input(true);

    /**
     * The choice as the dropdown below takes it. No guard against the value being written back is
     * needed the way `pace-instant-field` needs one: a choice is either made or not, so there is
     * no half-made state that a round trip could lose.
     */
    protected readonly selection = computed(() => (this.value() ? [this.value()] : []));

    /**
     * @param selection the dropdown's new selection, of at most one option
     */
    protected onSelectionChange(selection: string[]): void {
        this.value.set(selection[0] ?? '');
    }
}
